import { memo, useCallback, useEffect, useMemo } from 'react';
import {
  APIProvider,
  AdvancedMarker,
  ColorScheme,
  Map,
  Polyline,
} from '@vis.gl/react-google-maps';
import type {
  FleetMapErrorCode,
  FleetMapInit,
  FleetMapStop,
  FleetMapTrail,
  FleetMapVehicle,
} from '@bonbon/types';
import type { GoogleMapsConfig } from './config';
import {
  buildMarkerModels,
  buildStopModels,
  trailPath,
  type StopMarkerModel,
  type VehicleMarkerModel,
} from './markers';
import { MapViewport } from './MapViewport';

/** The Netherlands, until the first positions arrive. */
const DEFAULT_CENTER = { lat: 52.1, lng: 5.3 };
const DEFAULT_ZOOM = 7;
const TRAIL_COLOR = '#1976d2';

// Vans are drawn over the stops: a stop at the depot never hides the van on it.
const STOP_Z = 1;
const STOP_SELECTED_Z = 2;
const VAN_Z = 3;
const VAN_SELECTED_Z = 4;

// APIProvider re-runs its loader when `libraries` or `onError` change identity,
// and its default `libraries` is a new array on every render. After a failed
// load that would add another Google script tag on every render.
const LIBRARIES: string[] = [];

export interface FleetMapViewProps {
  config: GoogleMapsConfig;
  init: FleetMapInit;
  vehicles: readonly FleetMapVehicle[];
  stops: readonly FleetMapStop[];
  trail: FleetMapTrail | null;
  selectedId: string | null;
  selectedStopId: string | null;
  now: Date;
  onSelect: (telematicsVehicleId: string | null) => void;
  onSelectStop: (stopId: string | null) => void;
  onError: (code: FleetMapErrorCode) => void;
}

function VanIcon({
  model,
  selected,
}: {
  model: VehicleMarkerModel;
  selected: boolean;
}): React.JSX.Element {
  return (
    <div
      className={selected ? 'van van--selected' : 'van'}
      role="img"
      aria-label={model.ariaLabel}
    >
      <svg viewBox="-14 -14 28 28" width="30" height="30" aria-hidden="true">
        {model.heading === null ? (
          <circle r="8" fill={model.color} stroke="#fff" strokeWidth="2" />
        ) : (
          <path
            d="M0 -11 L8 9 L0 4.5 L-8 9 Z"
            fill={model.color}
            stroke="#fff"
            strokeWidth="2"
            strokeLinejoin="round"
            transform={`rotate(${model.heading})`}
          />
        )}
      </svg>
      <span className="van__plate">{model.title}</span>
    </div>
  );
}

function StopDot({
  model,
  selected,
}: {
  model: StopMarkerModel;
  selected: boolean;
}): React.JSX.Element {
  const classes = ['stop', `stop--${model.state}`, selected ? 'stop--selected' : '']
    .filter((name) => name !== '')
    .join(' ');
  return (
    <div
      className={classes}
      style={{ backgroundColor: model.color }}
      {...(model.ariaLabel === ''
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': model.ariaLabel })}
    >
      {model.label}
    </div>
  );
}

interface StopMarkerProps {
  model: StopMarkerModel;
  selected: boolean;
  onSelectStop: (stopId: string | null) => void;
}

/**
 * One stop. Memoised, with a click handler that only changes with the stop,
 * so the clock tick that redraws the vans does not redraw up to a thousand
 * stops with them.
 */
const StopMarker = memo(function StopMarker({
  model,
  selected,
  onSelectStop,
}: StopMarkerProps): React.JSX.Element {
  const { id } = model;
  const handleClick = useCallback((): void => {
    onSelectStop(id);
  }, [onSelectStop, id]);
  return (
    <AdvancedMarker
      position={model.position}
      zIndex={selected ? STOP_SELECTED_Z : STOP_Z}
      anchorTop="-50%"
      onClick={handleClick}
    >
      <StopDot model={model} selected={selected} />
    </AdvancedMarker>
  );
});

/**
 * The Google map. Mounted only after the host's `init`, so a visit to this
 * page that is not an embed loads nothing from Google and costs nothing.
 */
export function FleetMapView({
  config,
  init,
  vehicles,
  stops,
  trail,
  selectedId,
  selectedStopId,
  now,
  onSelect,
  onSelectStop,
  onError,
}: FleetMapViewProps): React.JSX.Element {
  const models = useMemo(
    () => buildMarkerModels(vehicles, init.labels, now),
    [vehicles, init.labels, now],
  );
  const stopModels = useMemo(
    () => buildStopModels(stops, init.labels),
    [stops, init.labels],
  );

  const handleLoadError = useCallback((): void => {
    onError('LOAD_FAILED');
  }, [onError]);

  useEffect(() => {
    window.gm_authFailure = () => {
      onError('AUTH_FAILED');
    };
    return () => {
      delete window.gm_authFailure;
    };
  }, [onError]);

  return (
    <APIProvider apiKey={config.apiKey} libraries={LIBRARIES} onError={handleLoadError}>
      <Map
        mapId={config.mapId}
        colorScheme={init.theme === 'dark' ? ColorScheme.DARK : ColorScheme.LIGHT}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        gestureHandling="greedy"
        clickableIcons={false}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        onClick={() => {
          // One message per click: the host takes few selections a second.
          if (selectedStopId !== null) onSelectStop(null);
          else onSelect(null);
        }}
      >
        <MapViewport
          vehicles={vehicles}
          stops={stops}
          trail={trail}
          selectedId={selectedId}
          selectedStopId={selectedStopId}
        />
        {trail !== null && (
          <Polyline
            path={trailPath(trail.points)}
            strokeColor={TRAIL_COLOR}
            strokeOpacity={0.85}
            strokeWeight={4}
            clickable={false}
          />
        )}
        {stopModels.map((model) => (
          <StopMarker
            key={model.id}
            model={model}
            selected={model.id === selectedStopId}
            onSelectStop={onSelectStop}
          />
        ))}
        {models.map((model) => {
          const selected = model.telematicsVehicleId === selectedId;
          return (
            <AdvancedMarker
              key={model.telematicsVehicleId}
              position={model.position}
              title={model.title}
              zIndex={selected ? VAN_SELECTED_Z : VAN_Z}
              anchorTop="-50%"
              onClick={() => {
                onSelect(model.telematicsVehicleId);
              }}
            >
              <VanIcon model={model} selected={selected} />
            </AdvancedMarker>
          );
        })}
      </Map>
    </APIProvider>
  );
}
